import { clientV3SDK, metadataOnlyQuery } from "@istari-frontend/api-client";
import type { Job, V3 } from "@istari/istari-client";
import { JobStatusName } from "@istari/istari-client";
import { type QueryClient, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { toast } from "sonner";

import { getJobFunctionName, getJobResourceLabel, playCompletionSound, showCompletionNotification } from "~/api/jobs";
import { applyJobEvents } from "~/jobs/data-source/apply-job-events";
import { resourceQueryKeys } from "~/resource/data-source/api/resource-query-options";

const OPTIMISTIC_JOBS_KEY = ["jobs:optimistic"] as const;

// Fan-out latency floor on the backend is ~15s, so polling much faster buys little freshness; the
// delta is cheap (usually empty), so 10s keeps it responsive without churn.
export const JOB_EVENTS_POLL_INTERVAL_MS = 10_000;

/**
 * Refresh resources/revisions a completed job may have produced. The event carries modelId, not
 * revision ids, so invalidate all resources (as the prior poll path did) plus the revision prefix
 * rather than specific revision caches. Called once per event page, not per terminal event, so a
 * page with several completions doesn't cancel/restart the same refetch repeatedly.
 */
export function invalidateDownstreamJobData(queryClient: QueryClient): void {
  void queryClient.invalidateQueries({ queryKey: resourceQueryKeys.all(), predicate: metadataOnlyQuery });
  void queryClient.invalidateQueries({ queryKey: ["revision"], predicate: metadataOnlyQuery });
}

export function handleTerminalJobEvent(queryClient: QueryClient, event: V3.JobStatusChangedEvent): void {
  // Toast/sound only for the user's own in-flight jobs (the optimistic set), matching the pre-event
  // behavior — not every visible job in the feed.
  const optimistic = queryClient.getQueryData<Job[]>(OPTIMISTIC_JOBS_KEY) ?? [];
  const match = optimistic.find((job) => job.modelId === event.modelId);
  if (!match) return;

  queryClient.setQueryData<Job[]>(
    OPTIMISTIC_JOBS_KEY,
    optimistic.filter((job) => job.modelId !== event.modelId),
  );

  const functionLabel = getJobFunctionName(match);
  const resourceLabel = getJobResourceLabel(match);
  if (event.status === JobStatusName.Completed) {
    const message = functionLabel
      ? `Job ${functionLabel} completed for ${resourceLabel}`
      : `Job completed for ${resourceLabel}`;
    toast.success(message);
    playCompletionSound();
    showCompletionNotification({ title: "Job completed", body: message });
  } else {
    const statusLower = String(event.status).toLowerCase();
    const message = functionLabel
      ? `Job ${functionLabel} ${statusLower} for ${resourceLabel}`
      : `Job ${statusLower} for ${resourceLabel}`;
    toast.error(message);
  }
}

/**
 * Single app-wide poller for the live job-status feed (``GET /api/v3/job-events``).
 *
 * Holds the stream cursor client-side, patches the job caches in place from each event, and fires
 * completion side-effects on terminal transitions — replacing interval polling of the ``/jobs`` list.
 * ``has_more`` triggers an immediate re-poll (reconnect catch-up); otherwise it waits the interval.
 * Mount once (see JobEventsHost).
 */
export function useJobStatusEvents(options?: { enabled?: boolean; intervalMs?: number }): void {
  const queryClient = useQueryClient();
  const afterSeqRef = useRef<number | null>(null);
  const seenRef = useRef<Set<string>>(new Set());
  const enabled = options?.enabled ?? true;
  const intervalMs = options?.intervalMs ?? JOB_EVENTS_POLL_INTERVAL_MS;

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const schedule = (ms: number) => {
      if (!cancelled) timer = setTimeout(() => void poll(), ms);
    };

    const poll = async () => {
      try {
        const page = await clientV3SDK.listJobEvents(
          afterSeqRef.current == null ? {} : { afterSeq: afterSeqRef.current },
        );
        if (cancelled) return;
        afterSeqRef.current = page.nextSeq;
        const hadTerminal = applyJobEvents(queryClient, page.items, seenRef.current, (event) =>
          handleTerminalJobEvent(queryClient, event),
        );
        if (hadTerminal) invalidateDownstreamJobData(queryClient);
        schedule(page.hasMore ? 0 : intervalMs);
      } catch {
        // Keep the loop alive across transient errors (network blip, 5xx); retry next interval.
        if (!cancelled) schedule(intervalMs);
      }
    };

    void poll();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [queryClient, enabled, intervalMs]);
}
